// Exact arithmetic on the decimal strings the API sends.
//
// ch.02: "amounts as numeric decimals ... Do not use JavaScript floating-point
// arithmetic for financial decisions." A bank-matching screen has to show the
// difference between a transaction and the invoices it would settle, and
// `Number(a) - Number(b)` is exactly the forbidden thing: 0.1 + 0.2 is not 0.3
// in binary floating point, and a residual of one cent is the difference
// between an exact match and one that must stay in review.
//
// So the amounts never become numbers. They are parsed into scaled BigInt —
// integer units of 10^-6, which is what `numeric(20,6)` stores — added and
// subtracted exactly, and rendered back to a string. BigInt has no rounding and
// no precision limit to run out of.
//
// This is arithmetic for DISPLAY and for one decision: whether a difference is
// zero. The authoritative comparison happens in the adapter, against the
// provider's own balance, immediately before anything is sent. Nothing here is
// allowed to become an approval criterion the server does not also enforce.

import { decimal, money as formatMoney } from './format.ts';

/** The scale of app.numeric(20,6). */
const SCALE = 6;
const SCALE_FACTOR = 10n ** BigInt(SCALE);

export class MoneyFormatError extends Error {
  constructor(value: string) {
    super(`not a decimal amount: ${value}`);
    this.name = 'MoneyFormatError';
  }
}

/** A decimal string to scaled units. Throws rather than guessing. */
export function toUnits(amount: string): bigint {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(amount.trim());
  if (match === null) throw new MoneyFormatError(amount);
  const [, sign = '', whole = '0', fraction = ''] = match;
  if (fraction.length > SCALE) {
    // More precision than the column holds. Truncating would silently discard
    // money; the caller has been handed something this app cannot represent.
    throw new MoneyFormatError(amount);
  }
  const padded = fraction.padEnd(SCALE, '0');
  const magnitude = BigInt(whole) * SCALE_FACTOR + BigInt(padded === '' ? '0' : padded);
  return sign === '-' ? -magnitude : magnitude;
}

/** Scaled units back to the decimal string shape the API uses. */
export function fromUnits(units: bigint): string {
  const negative = units < 0n;
  const magnitude = negative ? -units : units;
  const whole = magnitude / SCALE_FACTOR;
  const fraction = (magnitude % SCALE_FACTOR).toString().padStart(SCALE, '0');
  return `${negative ? '-' : ''}${whole.toString()}.${fraction}`;
}

export function absolute(amount: string): string {
  const units = toUnits(amount);
  return fromUnits(units < 0n ? -units : units);
}

export function sum(amounts: readonly string[]): string {
  return fromUnits(amounts.reduce((total, amount) => total + toUnits(amount), 0n));
}

export function subtract(left: string, right: string): string {
  return fromUnits(toUnits(left) - toUnits(right));
}

export function isZero(amount: string): boolean {
  return toUnits(amount) === 0n;
}

export function isNegative(amount: string): boolean {
  return toUnits(amount) < 0n;
}

/**
 * For display, in Dutch notation: "1234.500000" → "1.234,50". Trailing zeroes
 * below the second decimal are dropped, and anything a currency would not have
 * keeps its digits rather than being rounded away — a residual of 0.000001
 * must not render as 0,00. See format.ts, which owns the notation.
 */
export function forDisplay(amount: string): string {
  return decimal(amount);
}

/** "€ 121,00": the euro as its sign, any other currency by the code the API reported. */
export function format(value: { readonly amount: string; readonly currency: string }): string {
  return formatMoney(value);
}
